import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { authGetRequest } from 'helpers/apiRequests';
import { createLayer, clearMapLayer } from 'utils/map/layers';
import { hasObjectKeys, parseDate } from 'utils/main';
import {
  addLayerToMap,
  getAllClickedFeatures,
  getAllFeaturesByDetail,
  zoomToGeojsonGeometryWithBuffer,
  removeComponentLayers,
} from 'utils/map/map';
import useStyles from 'consts/styles/RightScreenPart';
import CustomizedTable from 'components/common/components/tablev2/CustomizedTable';
import { userStyles } from 'consts/styles/User';
import Icons from 'consts/icons';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import { addOperationsToLayer } from './utils/utils';
import DisplayOperationApprovalHistoryDetails from './DisplayHistoryDetails';

const OperationsApprovalsHistory = props => {
  const {
    filtersDataObject,
    filtersDateTimeOperations,
    activeDetails,
    setActiveDetails,
    checkItem,
  } = props;
  const timezone = useSelector(state => state.timezone);
  const map = useSelector(state => state.map);
  const [approvals, setApprovals] = React.useState([]);
  const [idItemOnClick, setIdItemOnClick] = React.useState(null);
  const [totalRows, setTotalRows] = React.useState(0);
  const [selectedOperation, setSelectedOperation] = React.useState(null);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [currentPage, setCurrentPage] = React.useState(0);
  const layer = React.useRef(null);

  const subtitles = useSelector(state => state.language).operationsApprovals;
  const closeSubtite = useSelector(state => state.language).close_;

  const classes = useStyles();
  const classesUser = userStyles();

  React.useEffect(() => {
    getApprovals();
    layer.current = createLayer('operationsApprovals');
  }, []);

  const initMap = () => {
    if (!hasObjectKeys(map)) return;
    addLayerToMap(map, layer.current);
    map.on('click', handleClick);
    return () => cleanup();
  };

  const handleClick = event => {
    if (!event) return;
    setIdItemOnClick(
      getAllFeaturesByDetail(
        getAllClickedFeatures(map, event),
        'approval'
      )[0]?.id_.substring(0, 36)
    );
  };

  const cleanup = () => {
    map.un('click', handleClick);
    removeComponentLayers(map);
  };

  React.useEffect(initMap, [map, checkItem]);

  React.useEffect(() => {
    if (layer.current !== null) addOperationsToLayer(layer.current, approvals);
  }, [approvals]);

  React.useEffect(() => {
    if (selectedOperation) {
      clearMapLayer(layer.current);
      addOperationsToLayer(layer.current, [selectedOperation]);
      setActiveDetails(true);
    } else {
      clearMapLayer(layer.current);
      addOperationsToLayer(layer.current, approvals);
    }
  }, [selectedOperation]);

  React.useEffect(() => {
    if (activeDetails !== true) setSelectedOperation(null);
  }, [activeDetails]);

  const rowClicked = data => {
    setSelectedOperation(
      approvals.find(operation => operation.uid === data.id)
    );
  };

  React.useEffect(() => {
    if (idItemOnClick !== null) {
      setSelectedOperation(
        approvals.find(operation => operation.uid === idItemOnClick)
      );
    }
    return () => setIdItemOnClick(null);
  }, [idItemOnClick]);

  const getApprovals = async (filtersObj = {}) => {
    const result = await authGetRequest('operationsFilters', {
      ...filtersObj,
      page: currentPage,
      page_size: rowsPerPage,
    });
    const resultLength = await authGetRequest(
      'operationsFiltersLength',
      filtersObj
    );
    if (result?.status === 200) {
      clearMapLayer(layer.current);
      setApprovals(result.data);
    }
    if (resultLength?.status === 200) setTotalRows(resultLength.data);
  };

  const handleZoom = () => {
    zoomToGeojsonGeometryWithBuffer(map, selectedOperation.geojson);
  };

  React.useEffect(() => {
    const filtersData = { ...filtersDataObject, ...filtersDateTimeOperations };
    getApprovals(filtersData);
  }, [filtersDataObject, filtersDateTimeOperations, rowsPerPage, currentPage]);

  return (
    <>
      <div className={`${classes.Wrapper}__history-wrapper`}>
        <CustomizedTable
          headers={[`${subtitles.uid_}`, 'Start', 'Stop', 'Status']}
          data={approvals.map(approval => ({
            id: approval.uid,
            start: parseDate(approval.start, timezone),
            stop: parseDate(approval.stop, timezone),
            status: approval.dtm_status,
          }))}
          onClickFunction={rowClicked}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={totalRows}
          selectedRow={selectedOperation}
          filtersDataObject={filtersDataObject}
          idItemOnClick={idItemOnClick}
          filtersUrl="operationsFilters"
        />
      </div>
      {activeDetails && selectedOperation ? (
        <Grid
          container
          className={`${classesUser.User}__dashboard ${classesUser.User}`}
        >
          <Tooltip title={closeSubtite} placement="bottom">
            <div
              className={`${classesUser.User}__settings__close`}
              onClick={() => setSelectedOperation(null)}
              onKeyPress={() => setSelectedOperation(null)}
              role="button"
              tabIndex={0}
            >
              <div
                className={`${classesUser.User}__settings__close__container`}
              >
                <Icons.Actions.Close />
              </div>
            </div>
          </Tooltip>
          <DisplayOperationApprovalHistoryDetails
            selectedOperation={selectedOperation}
            setSelectedOperation={setSelectedOperation}
            handleZoom={handleZoom}
          />
        </Grid>
      ) : null}
    </>
  );
};

OperationsApprovalsHistory.propTypes = {
  filtersDataObject: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  filtersDateTimeOperations: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  activeDetails: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  setActiveDetails: PropTypes.func,
  checkItem: PropTypes.object,
};
export default OperationsApprovalsHistory;
